"use client"

import {
  Grid,
  Typography,
  Paper,
  CircularProgress,
  Box,
  TextField,
  Card,
  Container,
  alpha,
  useTheme,
  Fade,
  Skeleton,
  Divider,
  IconButton,
  Tooltip,
  Stack,
} from "@mui/material"
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts"
import { useEffect, useState } from "react"
import { CalendarMonth, Refresh, People, Store, CardGiftcard, Campaign, TrendingUp } from "@mui/icons-material"

const AdminStats = () => {
  const theme = useTheme()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [startDate, setStartDate] = useState("")
  const [endDate, setEndDate] = useState("")

  const fetchData = () => {
    setLoading(true)
    let url = "/api/v2/admin/stats"
    if (startDate && endDate) {
      url += `?fromDate=${startDate}&toDate=${endDate}`
    }
    if (startDate && !endDate) {
      url += `?fromDate=${startDate}`
    }
    if (!startDate && endDate) {
      url += `?toDate=${endDate}`
    }
    fetch(url)
      .then((res) => res.json())
      .then((data) => {
        setData(data)
        setLoading(false)
      })
      .catch((err) => {
        console.error("Error fetching stats:", err)
        setLoading(false)
      })
  }

  useEffect(() => {
    fetchData()
  }, [startDate, endDate])

  // Custom colors for charts and cards
  const colors = {
    clients: theme.palette.primary.main,
    stores: "#FF6B6B",
    gifts: "#4ECDC4",
    announcements: "#FFD166",
    chartColors: ["#6E4CC5", "#FF6B6B", "#4ECDC4", "#FFD166", "#845EC2"],
  }

  // Stats data with icons and colors
  const statsData = [
    {
      title: "Active Clients",
      value: data?.totalActiveClients || 0,
      icon: <People />,
      color: colors.clients,
      subtitle: "Total registered users",
    },
    {
      title: "Active Stores",
      value: data?.totalActiveStores || 0,
      icon: <Store />,
      color: colors.stores,
      subtitle: "Operational locations",
    },
    {
      title: "Gifts Redeemed",
      value: data?.totalGiftsRedeemed || 0,
      icon: <CardGiftcard />,
      color: colors.gifts,
      subtitle: "Total redemptions",
    },
    {
      title: "Announcements",
      value: data?.totalAnnouncements || 0,
      icon: <Campaign />,
      color: colors.announcements,
      subtitle: "Active promotions",
    },
  ]

  if (loading && !data) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ mb: 4 }}>
          <Skeleton variant="rectangular" height={80} sx={{ borderRadius: 2 }} />
        </Box>
        <Grid container spacing={3}>
          {[1, 2, 3, 4].map((item) => (
            <Grid item xs={12} sm={6} md={3} key={item}>
              <Skeleton variant="rectangular" height={160} sx={{ borderRadius: 3 }} />
            </Grid>
          ))}
          <Grid item xs={12}>
            <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 3 }} />
          </Grid>
        </Grid>
      </Container>
    )
  }

  return (
    <Fade in={true} timeout={800}>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box
          sx={{
            mb: 4,
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Box>
              <Typography variant="h4" fontWeight="bold" color="text.primary">
                Admin Dashboard
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Overview of system performance and statistics
              </Typography>
            </Box>
            <Tooltip title="Refresh data">
              <IconButton
                onClick={fetchData}
                sx={{
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  "&:hover": {
                    bgcolor: alpha(theme.palette.primary.main, 0.2),
                  },
                }}
              >
                <Refresh />
              </IconButton>
            </Tooltip>
          </Box>

          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 3,
              boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
              backgroundColor: "#ffffff",
              border: "1px solid",
              borderColor: alpha(theme.palette.divider, 0.1),
            }}
          >
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle1" fontWeight="medium" sx={{ mb: 1 }}>
                  <CalendarMonth sx={{ mr: 1, verticalAlign: "middle", color: theme.palette.primary.main }} />
                  Filter by Date Range
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Select dates to view specific period statistics
                </Typography>
              </Grid>
              <Grid item xs={12} md={8}>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Start Date"
                    value={startDate}
                    InputLabelProps={{ shrink: true }}
                    onChange={(event) => setStartDate(event.target.value)}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.background.paper, 0.8),
                      },
                    }}
                  />
                  <TextField
                    fullWidth
                    type="date"
                    label="End Date"
                    value={endDate}
                    InputLabelProps={{ shrink: true }}
                    onChange={(event) => setEndDate(event.target.value)}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.background.paper, 0.8),
                      },
                    }}
                  />
                </Stack>
              </Grid>
            </Grid>
          </Paper>
        </Box>

        <Grid container spacing={3}>
          {statsData.map((stat, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card
                sx={{
                  borderRadius: 3,
                  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
                  overflow: "hidden",
                  height: "100%",
                  position: "relative",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-5px)",
                    boxShadow: "0 10px 30px rgba(0, 0, 0, 0.12)",
                  },
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "4px",
                    backgroundColor: stat.color,
                  },
                }}
              >
                <Box sx={{ p: 3 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        {stat.title}
                      </Typography>
                      <Typography variant="h4" fontWeight="bold" sx={{ mb: 1 }}>
                        {loading ? <Skeleton width={60} /> : stat.value.toLocaleString()}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {stat.subtitle}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 48,
                        height: 48,
                        borderRadius: 2,
                        backgroundColor: alpha(stat.color, 0.12),
                        color: stat.color,
                      }}
                    >
                      {stat.icon}
                    </Box>
                  </Box>
                </Box>
              </Card>
            </Grid>
          ))}

          <Grid item xs={12} md={8}>
            <Card
              sx={{
                borderRadius: 3,
                boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
                overflow: "hidden",
                height: "100%",
              }}
            >
              <Box sx={{ p: 3 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
                  <Box>
                    <Typography variant="h6" fontWeight="bold">
                      Gifts Redeemed by Status
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Distribution of gift redemption statuses
                    </Typography>
                  </Box>
                  <TrendingUp color="primary" />
                </Box>
                <Divider sx={{ mb: 3 }} />
                <Box sx={{ height: 350, width: "100%" }}>
                  {loading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                      <CircularProgress />
                    </Box>
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={data?.totalGiftOrdersByStatus || []}>
                        <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.3)} />
                        <XAxis
                          dataKey="status"
                          tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
                          axisLine={{ stroke: alpha(theme.palette.divider, 0.5) }}
                        />
                        <YAxis
                          tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
                          axisLine={{ stroke: alpha(theme.palette.divider, 0.5) }}
                        />
                        <RechartsTooltip
                          contentStyle={{
                            borderRadius: 8,
                            backgroundColor: theme.palette.background.paper,
                            border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
                          }}
                          cursor={{ fill: alpha(theme.palette.primary.main, 0.1) }}
                        />
                        <Legend
                          wrapperStyle={{ paddingTop: 20 }}
                          formatter={(value) => <span style={{ color: theme.palette.text.primary }}>{value}</span>}
                        />
                        <Bar dataKey="count" name="Number of Orders" radius={[4, 4, 0, 0]}>
                          {(data?.totalGiftOrdersByStatus || []).map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={colors.chartColors[index % colors.chartColors.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </Box>
              </Box>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card
              sx={{
                borderRadius: 3,
                boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
                overflow: "hidden",
                height: "100%",
              }}
            >
              <Box sx={{ p: 3 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
                  <Box>
                    <Typography variant="h6" fontWeight="bold">
                      Status Distribution
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Percentage breakdown by status
                    </Typography>
                  </Box>
                  <CardGiftcard color="primary" />
                </Box>
                <Divider sx={{ mb: 3 }} />
                <Box sx={{ height: 350, width: "100%" }}>
                  {loading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                      <CircularProgress />
                    </Box>
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={data?.totalGiftOrdersByStatus || []}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          outerRadius={120}
                          innerRadius={60}
                          dataKey="count"
                          nameKey="status"
                          label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                        >
                          {(data?.totalGiftOrdersByStatus || []).map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={colors.chartColors[index % colors.chartColors.length]} />
                          ))}
                        </Pie>
                        <RechartsTooltip
                          contentStyle={{
                            borderRadius: 8,
                            backgroundColor: theme.palette.background.paper,
                            border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  )}
                </Box>
              </Box>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Fade>
  )
}

export default AdminStats

